import { ArrowPathIcon } from '@heroicons/react/20/solid';

import { Button, EButtonType } from '@/components/Button';
import { DisplayComponent } from '@/components/DisplayComponent';
import { Header } from '@/components/Header';
import { OutcomeCard } from '@/components/OutcomeCard';
import { ProgressBar } from '@/components/ProgressBar';
import { EDisplays, selectPartnerNumber, setDisplay } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { PHONE } from '@/utils/constants';

export const testId = 'screen-servicingTechnicalFailure';

/**
 * Shown when the affordability check could not be run.
 *
 * Deliberately separate from ServicingFailure: that screen tells the customer
 * their income may not be high enough, which is not something we know when the
 * request never completed.
 */
export function ServicingTechnicalFailure(): JSX.Element {
    const baseId = 'screen-servicingTechnicalFailure';
    const dispatch = useAppDispatch();
    const { technicalFailure } = useCopy();
    const phoneNumber = useAppSelector(selectPartnerNumber) || PHONE;

    // ***** Event Handlers *****
    function handleTryAgain(): void {
        // Back to the screen that starts the check, so pressing on from there
        // runs it again rather than replaying a request from a dead end.
        dispatch(setDisplay(EDisplays.INCOME));
    }

    function handleBackToStart(): void {
        dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
    }

    // ***** Render *****
    return (
        <div data-testid={testId}>
            <DisplayComponent baseId={baseId}>
                <Header />
                <ProgressBar />
                <OutcomeCard
                    icon={<ArrowPathIcon className='h-6 w-6' />}
                    tint='bg-optionTrack text-panel'
                    title={technicalFailure.title}
                    lines={[]}
                >
                    <p className='text-sm text-textBody'>
                        {technicalFailure.bodyPrefix}{' '}
                        {/* The number is partner config, not copy. */}
                        <a
                            href={`tel:${phoneNumber}`}
                            id={`${baseId}-link-call`}
                            className='font-bold text-panel underline'
                        >
                            {phoneNumber}
                        </a>{' '}
                        {technicalFailure.bodySuffix}
                    </p>
                    <Button
                        id={`${baseId}-button-tryAgain`}
                        onClick={handleTryAgain}
                        type={EButtonType.CARD_PRIMARY}
                    >
                        <span className='text-[15px]'>
                            {technicalFailure.cta}
                        </span>
                    </Button>
                </OutcomeCard>
                <div className='_flex pt-3 pb-2'>
                    <Button
                        id={`${baseId}-button-backToStart`}
                        onClick={handleBackToStart}
                        type={EButtonType.SECONDARY_OUTLINE}
                    >
                        <p className='text-sm'>
                            {technicalFailure.backToStart}
                        </p>
                    </Button>
                </div>
            </DisplayComponent>
        </div>
    );
}
